import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { errorHandler } from 'hooks/errorHandler'
import axios from 'axios'
import {
  WORKFLOW_FILE_CHUNK_SIZE,
  WORKFLOW_FILE_REMOVE_BATCH_SIZE,
  type WorkflowFileItem,
  type WorkflowFileUploadProgress,
} from '@cims-online/shared'
import type {
  WorkflowFileBranch,
  WorkflowFileQueueEntry,
  WorkflowRecordFileManagerProps,
} from '../types'
import {
  createWorkflowFileOperations,
  isWorkflowFileSessionBusy,
  workflowFileRequestError,
} from 'utils/workflowRecordFiles'

/** How long a completed upload stays in the queue so users see it finish. */
const COMPLETED_ENTRY_DISPLAY_MS = 1500

/** Consecutive responses without progress before an upload is abandoned. */
const MAX_STAGNANT_RESPONSES = 3

/** Session outcomes that supersede a failed request's error. */
const RETAINED_STATES = new Set<WorkflowFileUploadProgress['state']>([
  'uploaded',
  'replaced',
  'conflict',
  'unverified',
])

/**
 * Controls one record's file manager session: the upload queue, the lazily
 * loaded folder tree, selection, and removal.
 *
 * Uploads run sequentially. The tree preloads the root's immediate folders.
 * Responses that arrive after unmount are ignored, and unmounting cancels
 * staged upload sessions once any request still running on them finishes.
 */
export function useWorkflowRecordFiles({
  projectId,
  recordId,
  metadata,
  onDenied,
  onBusyChange,
}: WorkflowRecordFileManagerProps) {
  const operations = useMemo(
    () => createWorkflowFileOperations(projectId, recordId),
    [projectId, recordId]
  )
  const [branches, setBranches] = useState<Record<string, WorkflowFileBranch>>(
    {}
  )
  const [queue, setQueue] = useState<WorkflowFileQueueEntry[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [removing, setRemoving] = useState(false)
  const [refreshCount, setRefreshCount] = useState(0)
  const [denied, setDenied] = useState(false)
  const active = useRef(true)
  const abort = useRef(new AbortController())
  /** The authoritative queue; `queue` state mirrors it for rendering. */
  const entries = useRef<WorkflowFileQueueEntry[]>([])
  /** Upload session IDs by queue entry ID, cancelled on unmount. */
  const sessions = useRef(new Map<string, string>())
  const processing = useRef(false)
  const completionTimers = useRef(new Set<ReturnType<typeof setTimeout>>())
  /** Folder IDs by `/`-joined path relative to the record folder. */
  const folders = useRef(new Map<string, string>([['', metadata.root.id]]))
  /** Folders whose children have loaded, which refresh reloads. */
  const loaded = useRef(new Set<string>())
  /** In-flight child listings by folder ID. */
  const loading = useRef(
    new Map<string, Promise<WorkflowFileItem[] | undefined>>()
  )
  /** Incremented by each refresh so listings it supersedes are not committed. */
  const generation = useRef(0)
  const callbacks = useRef({ onDenied, onBusyChange })
  callbacks.current = { onDenied, onBusyChange }

  /** Shows a failure in the standard client error toast and records authorization loss. */
  const report = useCallback((failure: unknown) => {
    if (!active.current || abort.current.signal.aborted) return
    const result = workflowFileRequestError(failure)
    if (axios.isCancel(failure)) errorHandler(result.message)
    else errorHandler(failure)
    if (result.denied) {
      setDenied(true)
      callbacks.current.onDenied()
    }
  }, [])
  /**
   * Applies changes to one queue entry.
   *
   * Successful entries notify the user and are briefly retained so the
   * completed progress bar is visible.
   */
  const update = useCallback(
    (id: string, changes: Partial<WorkflowFileQueueEntry>) => {
      const previous = entries.current.find((entry) => entry.id === id)
      entries.current = entries.current.map((entry) =>
        entry.id === id ? { ...entry, ...changes } : entry
      )
      if (!active.current) return
      setQueue(entries.current)
      if (
        previous &&
        previous.state !== 'uploaded' &&
        previous.state !== 'replaced' &&
        (changes.state === 'uploaded' || changes.state === 'replaced')
      ) {
        if (previous.file)
          simpleSuccessNotification(
            `${previous.file.name} uploaded successfully!`
          )
        const timer = setTimeout(() => {
          completionTimers.current.delete(timer)
          if (!active.current) return
          entries.current = entries.current.filter((entry) => entry.id !== id)
          setQueue(entries.current)
        }, COMPLETED_ENTRY_DISPLAY_MS)
        completionTimers.current.add(timer)
      }
    },
    []
  )
  /**
   * Shares in-flight listings between background preloading and folder expansion.
   *
   * A listing that a refresh supersedes is requested again rather than
   * committed, because it may predate the change that triggered the refresh.
   */
  const loadBranch = useCallback(
    (folderId: string) => {
      const list = (): Promise<WorkflowFileItem[] | undefined> => {
        const pending = loading.current.get(folderId)
        if (pending) return pending
        const signal = abort.current.signal
        const listing = generation.current
        const request = (async () => {
          try {
            const response = await operations.children(folderId, signal)
            if (!active.current || signal.aborted) return
            if (listing !== generation.current) return list()
            loaded.current.add(folderId)
            setBranches((previous) => ({
              ...previous,
              [folderId]: { items: response.items, error: null },
            }))
            return response.items
          } catch (failure) {
            if (!active.current || signal.aborted) return
            if (listing !== generation.current) return list()
            setBranches((previous) => ({
              ...previous,
              [folderId]: {
                items: previous[folderId]?.items ?? [],
                error: workflowFileRequestError(failure).message,
              },
            }))
            report(failure)
            return undefined
          }
        })().finally(() => {
          if (loading.current.get(folderId) === request)
            loading.current.delete(folderId)
        })
        loading.current.set(folderId, request)
        return request
      }
      return list()
    },
    [operations, report]
  )
  /** Publishes the root first, then preloads only its immediate folders one at a time. */
  const load = useCallback(
    async (folderId: string) => {
      const signal = abort.current.signal
      const items = await loadBranch(folderId)
      if (folderId !== metadata.root.id || !items) return
      for (const item of items) {
        if (!active.current || signal.aborted) return
        if (item.kind === 'folder' && !loaded.current.has(item.id))
          await loadBranch(item.id)
      }
    },
    [loadBranch, metadata.root.id]
  )
  /**
   * Reloads the root, its immediate folders, and previously opened deeper
   * branches.
   *
   * Only the most recently started refresh commits, so an overlapping older
   * one cannot replace newer contents. A successful refresh also drops stale
   * selection, including items deleted before a removal response was lost.
   */
  const refresh = useCallback(async () => {
    const signal = abort.current.signal
    if (!active.current || signal.aborted) return
    const current = ++generation.current
    loading.current.clear()
    setRefreshCount((count) => count + 1)
    folders.current = new Map([['', metadata.root.id]])
    try {
      const next: Record<string, WorkflowFileBranch> = {}
      const pending = [metadata.root.id]
      const visited = new Set<string>()
      while (
        pending.length &&
        active.current &&
        current === generation.current
      ) {
        const folderId = pending.shift()!
        if (visited.has(folderId)) continue
        visited.add(folderId)
        try {
          const branch = await operations.children(folderId, signal)
          next[folderId] = { items: branch.items, error: null }
          for (const item of branch.items)
            if (
              item.kind === 'folder' &&
              (folderId === metadata.root.id || loaded.current.has(item.id))
            )
              pending.push(item.id)
        } catch (failure) {
          if (
            !active.current ||
            signal.aborted ||
            current !== generation.current
          )
            return
          if (workflowFileRequestError(failure).denied) throw failure
          next[folderId] = {
            items: [],
            error: workflowFileRequestError(failure).message,
          }
          report(failure)
        }
      }
      if (active.current && !signal.aborted && current === generation.current) {
        loaded.current = new Set(Object.keys(next))
        setBranches(next)
        if (Object.values(next).every((branch) => !branch.error)) {
          const visibleIds = new Set(
            Object.values(next).flatMap((branch) =>
              branch.items.map((item) => item.id)
            )
          )
          setSelected((ids) => ids.filter((id) => visibleIds.has(id)))
        }
      }
    } catch (failure) {
      if (!signal.aborted) report(failure)
    } finally {
      if (active.current && !signal.aborted)
        setRefreshCount((count) => count - 1)
    }
  }, [operations, metadata.root.id, report])

  /**
   * Uploads queued entries one at a time until the queue is drained, then
   * refreshes the tree if anything changed.
   */
  const run = useCallback(async () => {
    if (processing.current || !active.current) return
    processing.current = true
    callbacks.current.onBusyChange(true)
    let effects = false
    try {
      while (active.current) {
        const entry = entries.current.find((item) => item.state === 'queued')
        if (!entry) break
        update(entry.id, { state: 'uploading', message: null })
        effects = true
        const uploadAbort = new AbortController()
        const closeAbort = () => uploadAbort.abort()
        abort.current.signal.addEventListener('abort', closeAbort, {
          once: true,
        })
        let sessionId: string | null = null
        try {
          // Sequential processing preserves same-destination ordering and folder dependencies.
          let parentId = metadata.root.id
          for (let depth = 1; depth < entry.path.length; depth++) {
            const key = entry.path.slice(0, depth).join('/')
            let id = folders.current.get(key)
            if (!id) {
              const folder = await operations.folder(
                parentId,
                entry.path[depth - 1],
                uploadAbort.signal
              )
              if (!active.current) return
              id = folder.id
              folders.current.set(key, id)
              effects = true
            }
            parentId = id
          }
          if (!entry.file) {
            const folder = await operations.folder(
              parentId,
              entry.path[entry.path.length - 1],
              uploadAbort.signal
            )
            if (!active.current) return
            folders.current.set(entry.path.join('/'), folder.id)
            effects = true
            update(entry.id, {
              state: 'uploaded',
              message: null,
            })
            continue
          }
          let progress = await operations.initialize(
            parentId,
            entry.file,
            uploadAbort.signal
          )
          sessionId = progress.sessionId
          if (sessionId) sessions.current.set(entry.id, sessionId)
          if (!active.current) {
            if (sessionId)
              await operations.cancel(sessionId).catch(() => undefined)
            return
          }
          effects = true
          let stagnant = 0
          while (
            (progress.state === 'uploading' || progress.state === 'staged') &&
            sessionId &&
            active.current
          ) {
            const previous = progress.offset
            if (progress.state === 'staged')
              progress = await operations.complete(
                sessionId,
                uploadAbort.signal
              )
            else {
              const bytes = entry.file.slice(
                progress.offset,
                progress.offset + WORKFLOW_FILE_CHUNK_SIZE
              )
              try {
                progress = await operations.chunk(
                  sessionId,
                  progress.offset,
                  bytes,
                  uploadAbort.signal,
                  (loaded) => {
                    if (!active.current || uploadAbort.signal.aborted) return
                    update(entry.id, { sentOffset: previous + loaded })
                  }
                )
              } catch (failure) {
                if (workflowFileRequestError(failure).denied) throw failure
                // The server may have accepted the chunk; resume from its offset.
                progress = await operations.status(
                  sessionId,
                  uploadAbort.signal
                )
              }
            }
            if (!active.current) return
            update(entry.id, {
              offset: progress.offset,
              sentOffset: undefined,
            })
            stagnant = progress.offset === previous ? stagnant + 1 : 0
            if (
              stagnant >= MAX_STAGNANT_RESPONSES &&
              (progress.state === 'uploading' || progress.state === 'staged')
            )
              throw new Error(
                'SharePoint did not advance this upload. Retry the file.'
              )
          }
          if (active.current)
            update(entry.id, {
              state: queueState(progress),
              offset: progress.offset,
              message: progress.message,
            })
        } catch (failure) {
          /*
           * A lost response may hide an outcome the server retained, which
           * cancellation returns instead of cancelling. Cancellation waits for
           * any request still running on the session, so the queue does not
           * advance while that request could commit over a later upload.
           */
          let settled: WorkflowFileUploadProgress | undefined
          let unresolved = false
          if (sessionId)
            try {
              settled = await operations.cancel(sessionId)
            } catch (cancelFailure) {
              unresolved = isWorkflowFileSessionBusy(cancelFailure)
            }
          if (active.current) {
            const { message, denied } = workflowFileRequestError(failure)
            let reconciled = false
            let changes: Partial<WorkflowFileQueueEntry> = {
              state: 'failed',
              message,
            }
            if (settled && RETAINED_STATES.has(settled.state)) {
              reconciled = true
              changes = {
                state: queueState(settled),
                offset: settled.offset,
                message: settled.message,
              }
            } else if (unresolved)
              changes = {
                state: 'unverified',
                message:
                  'An earlier request for this upload is still running. Refresh the folder before retrying this file.',
              }
            update(entry.id, changes)
            if (!reconciled || denied) report(failure)
          }
          if (workflowFileRequestError(failure).denied) {
            entries.current
              .filter((item) => item.state === 'queued')
              .forEach((item) => update(item.id, { state: 'cancelled' }))
            break
          }
        } finally {
          abort.current.signal.removeEventListener('abort', closeAbort)
          sessions.current.delete(entry.id)
        }
      }
    } finally {
      processing.current = false
      if (active.current) {
        callbacks.current.onBusyChange(false)
        if (effects) await refresh()
      }
    }
  }, [metadata.root.id, operations, refresh, report, update])

  /** Queues entries and starts uploading if the queue is idle. */
  const add = useCallback(
    (items: WorkflowFileQueueEntry[]) => {
      entries.current = [...entries.current, ...items]
      setQueue(entries.current)
      void run()
    },
    [run]
  )
  /** Requeues an entry as a new attempt, re-resolving its parent folders. */
  const retry = useCallback(
    (id: string) => {
      folders.current = new Map([['', metadata.root.id]])
      update(id, {
        state: 'queued',
        offset: 0,
        sentOffset: undefined,
        message: null,
      })
      void run()
    },
    [metadata.root.id, run, update]
  )
  /**
   * Removes the selected items in server-sized batches, then refreshes the
   * tree. Items that fail to be removed remain selected.
   */
  const remove = useCallback(
    async (ids: string[]) => {
      if (!active.current) return
      setRemoving(true)
      callbacks.current.onBusyChange(true)
      const failed: string[] = []
      const selectedIds = new Set(ids)
      // Skip items inside a selected folder; removing the folder covers them.
      const parents = new Map(
        Object.values(branches).flatMap((branch) =>
          branch.items.map((item) => [item.id, item.parentId] as const)
        )
      )
      const targets = [...selectedIds].filter((id) => {
        const visited = new Set<string>()
        let parentId = parents.get(id)
        while (parentId && !visited.has(parentId)) {
          if (selectedIds.has(parentId)) return false
          visited.add(parentId)
          parentId = parents.get(parentId)
        }
        return true
      })
      try {
        for (
          let i = 0;
          i < targets.length && active.current;
          i += WORKFLOW_FILE_REMOVE_BATCH_SIZE
        ) {
          const result = await operations.remove(
            targets.slice(i, i + WORKFLOW_FILE_REMOVE_BATCH_SIZE),
            abort.current.signal
          )
          for (const outcome of result.outcomes)
            if (
              outcome.state !== 'removed' &&
              outcome.state !== 'alreadyAbsent'
            ) {
              failed.push(outcome.id)
              report(outcome.message ?? 'Unable to remove the selected item.')
            }
        }
        if (active.current) setSelected(failed)
      } catch (failure) {
        report(failure)
      } finally {
        if (active.current) {
          setRemoving(false)
          callbacks.current.onBusyChange(false)
          await refresh()
        }
      }
    },
    [branches, operations, refresh, report]
  )

  useEffect(() => {
    active.current = true
    // Cleanup permanently aborts its controller; each effect needs a fresh one.
    const requestAbort = new AbortController()
    abort.current = requestAbort
    loading.current.clear()
    const pendingSessions = sessions.current
    const pendingCompletionTimers = completionTimers.current
    void load(metadata.root.id)
    return () => {
      active.current = false
      requestAbort.abort()
      for (const timer of pendingCompletionTimers) clearTimeout(timer)
      pendingCompletionTimers.clear()
      for (const sessionId of pendingSessions.values())
        void operations.cancel(sessionId).catch(() => undefined)
    }
  }, [load, metadata.root.id, operations])

  return {
    branches,
    queue,
    selected,
    setSelected,
    removing,
    refreshing: refreshCount > 0,
    denied,
    load,
    refresh,
    add,
    retry,
    remove,
    report,
    busy:
      removing ||
      queue.some(
        (entry) => entry.state === 'queued' || entry.state === 'uploading'
      ),
  }
}

/** Maps unfinished provider results to a retryable queue failure. */
function queueState(
  progress: WorkflowFileUploadProgress
): WorkflowFileQueueEntry['state'] {
  if (progress.state === 'uploading' || progress.state === 'staged')
    return 'failed'
  return progress.state
}
